import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useParams, Link, useNavigate } from 'react-router-dom'
import {
	NavLink,
	Stack,
	ScrollArea,
	Box,
} from '@mantine/core'
import {
	Browser,
	ChartBar,
	ClipboardCheck,
	ClipboardList,
	InfoCircle,
	LayoutDashboard,
	Messages,
	Plus,
	Settings,
	ShoppingBag,
	Template,
	UserCheck,
	Users,
} from 'tabler-icons-react'
import { switchOrg } from '../services/actions/userActions'
import { getOrgDetails } from '../services/actions/orgActions'
import { loadUserContext } from '../services/utils/loadUserContext'

const ExperienceNavIcon = ({ icon: Icon }) => (
	<Icon size={16} strokeWidth={1.5} aria-hidden='true' focusable='false' />
)

const Sidebar = () => {
	const { experienceSlug, orgSlug } = useParams()
	const location = useLocation()
	const navigate = useNavigate()
	const dispatch = useDispatch()

	const currentOrgName = useSelector((state) => state.user.currentOrg?.orgName)
	const currentOrgSlug = useSelector((state) => state.user.currentOrg?.orgSlug)
	const userType = useSelector((state) => state.user.authUser?.userType)
	const orgRole = useSelector((state) => state.org.orgRole)
	const position = useSelector((state) => state.experience.userPosition)
	const experience = useSelector((state) => state.experience.experienceDetails)
	const [controlOrgName, setControlOrgName] = useState('')

	const isActive = (path) => location.pathname === path
	const orgSearchPath = `/${orgSlug}/search`
	const inAdminRoutes = location.pathname.startsWith(`/${orgSlug}/admin`) || isActive(orgSearchPath)
	const inControlRoutes = location.pathname.startsWith(`/control`)
	const inControlOrgsRoutes = location.pathname.startsWith(`/control/orgs/`)
	const adminCommercePath = `/${orgSlug}/admin/commerce`
	const adminBillingPath = `/${orgSlug}/admin/billing`
	const inAdminSettings =
		isActive(adminCommercePath) || isActive(adminBillingPath)
	const experienceOverviewPath = `/${orgSlug}/experiences/${experienceSlug}/overview`
	const experienceSalesPath = `${experienceOverviewPath}/sales`
	const managedOrgName =
		controlOrgName ||
		(currentOrgSlug === orgSlug ? currentOrgName : '') ||
		orgSlug ||
		'Organization'

	useEffect(() => {
		if (!inControlOrgsRoutes || !orgSlug) {
			setControlOrgName('')
			return
		}

		let cancelled = false

		const loadControlOrgName = async () => {
			try {
				const org = await getOrgDetails(orgSlug)
				if (!cancelled) setControlOrgName(org?.name || '')
			} catch (err) {
				if (!cancelled) setControlOrgName('')
				console.error('Failed to load control org name:', err)
			}
		}

		loadControlOrgName()

		return () => {
			cancelled = true
		}
	}, [inControlOrgsRoutes, orgSlug])

	const handleManageControlOrg = async () => {
		if (!orgSlug) return

		try {
			if (currentOrgSlug !== orgSlug) {
				await switchOrg(orgSlug)
				await loadUserContext(dispatch)
			}
			navigate(`/${orgSlug}/admin/experiences`)
		} catch (err) {
			console.error('Failed to switch organization:', err)
		}
	}

	return (
		<ScrollArea
			style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
			sx={{ '.mantine-NavLink-root': { borderRadius: 7 } }}
		>
			<Stack spacing={experienceSlug ? 6 : 'sm'} style={{ flexGrow: 1 }}>
				{userType === 'superAdmin' && inControlRoutes && (
					<>
						{inControlOrgsRoutes && (
							<>
								<NavLink
									label='Organization Details'
									component={Link}
									to={`/control/orgs/${orgSlug}/details`}
									active={isActive(`/control/orgs/${orgSlug}/details`)}
								/>
								<NavLink
									label='Plan & Billing'
									component={Link}
									to={`/control/orgs/${orgSlug}/billing`}
									active={isActive(`/control/orgs/${orgSlug}/billing`)}
								/>
								<NavLink
									label='Commerce'
									component={Link}
									to={`/control/orgs/${orgSlug}/commerce`}
									active={isActive(`/control/orgs/${orgSlug}/commerce`)}
								/>
								<NavLink
									label={`Manage ${managedOrgName}`}
									component='button'
									type='button'
									onClick={handleManageControlOrg}
								/>
							</>
						)}
					</>
				)}

				{(orgRole === 'admin' || userType === 'superAdmin') &&
					inAdminRoutes && (
						<>
							<NavLink
								label='Experiences'
								component={Link}
								to={`/${orgSlug}/admin/experiences`}
								active={isActive(`/${orgSlug}/admin/experiences`)}
							/>
							<NavLink
								label='Users'
								component={Link}
								to={`/${orgSlug}/admin/users`}
								active={isActive(`/${orgSlug}/admin/users`)}
							/>
							<NavLink
								label='Check-Ins'
								component={Link}
								to={`/${orgSlug}/admin/checkins`}
								active={isActive(`/${orgSlug}/admin/checkins`)}
							/>
							<NavLink
								label='Locations'
								component={Link}
								to={`/${orgSlug}/admin/locations`}
								active={isActive(`/${orgSlug}/admin/locations`)}
							/>
							<NavLink
								label='Settings'
								active={inAdminSettings}
								defaultOpened={inAdminSettings}
							>
								<NavLink
									label='Commerce'
									component={Link}
									to={adminCommercePath}
									active={isActive(adminCommercePath)}
								/>
								<NavLink
									label='Plan & Billing'
									component={Link}
									to={adminBillingPath}
									active={isActive(adminBillingPath)}
								/>
							</NavLink>
						</>
					)}

				{experienceSlug && (
					<>
						{(position?.level === 'owner' || orgRole === 'admin') && (
							<NavLink
								label='Overview'
								icon={<ExperienceNavIcon icon={LayoutDashboard} />}
								active={location.pathname.startsWith(experienceOverviewPath)}
								defaultOpened={location.pathname.startsWith(
									experienceOverviewPath,
								)}
							>
								<NavLink
									label='Check-Ins'
									icon={<ExperienceNavIcon icon={ClipboardCheck} />}
									component={Link}
									to={experienceOverviewPath}
									active={isActive(experienceOverviewPath)}
								/>
								<NavLink
									label='Sales'
									icon={<ExperienceNavIcon icon={ChartBar} />}
									component={Link}
									to={experienceSalesPath}
									active={location.pathname.startsWith(experienceSalesPath)}
								/>
							</NavLink>
						)}

						<NavLink
							label='Check-In Travelers'
							icon={<ExperienceNavIcon icon={UserCheck} />}
							component={Link}
							to={`/${orgSlug}/experiences/${experienceSlug}`}
							active={isActive(`/${orgSlug}/experiences/${experienceSlug}`)}
						/>

						{experience?.allowStaffAddCheckIns &&
							experience?.status !== 'inactive' &&
							position?.level === 'staff' && (
								<NavLink
									label='Add Check-In'
									icon={<ExperienceNavIcon icon={Plus} />}
									component={Link}
									to={`/${orgSlug}/experiences/${experienceSlug}/manage/checkin/new`}
									active={isActive(
										`/${orgSlug}/experiences/${experienceSlug}/manage/checkin/new`,
									)}
								/>
							)}

						{(position?.level === 'owner' ||
							orgRole === 'admin' ||
							userType === 'superAdmin') && (
							<>
								<NavLink
									label='Manage Check-Ins'
									icon={<ExperienceNavIcon icon={ClipboardList} />}
									component={Link}
									to={`/${orgSlug}/experiences/${experienceSlug}/manage/checkins`}
									active={isActive(
										`/${orgSlug}/experiences/${experienceSlug}/manage/checkins`,
									)}
								/>
								<NavLink
									label='Manage Staff'
									icon={<ExperienceNavIcon icon={Users} />}
									component={Link}
									to={`/${orgSlug}/experiences/${experienceSlug}/manage/staff`}
									active={isActive(
										`/${orgSlug}/experiences/${experienceSlug}/manage/staff`,
									)}
								/>
								<NavLink
									label='Messaging Center'
									icon={<ExperienceNavIcon icon={Messages} />}
									component={Link}
									to={`/${orgSlug}/experiences/${experienceSlug}/messaging`}
									active={isActive(
										`/${orgSlug}/experiences/${experienceSlug}/messaging`,
									)}
								/>
							</>
						)}

						{(orgRole === 'admin' || userType === 'superAdmin') && (
							<NavLink
								label='Experience Settings'
								icon={<ExperienceNavIcon icon={Settings} />}
								defaultOpened={location.pathname.includes(`/manage/`)} // auto-open based on route
							>
								<NavLink
									label='Details'
									icon={<ExperienceNavIcon icon={InfoCircle} />}
									component={Link}
									to={`/${orgSlug}/experiences/${experienceSlug}/manage/details`}
									active={isActive(
										`/${orgSlug}/experiences/${experienceSlug}/manage/details`,
									)}
								/>
									<NavLink
										label='Experience Portal'
										icon={<ExperienceNavIcon icon={Browser} />}
									component={Link}
									to={`/${orgSlug}/experiences/${experienceSlug}/manage/portal`}
									active={isActive(
										`/${orgSlug}/experiences/${experienceSlug}/manage/portal`,
									)}
									/>
									<NavLink
										label='Experience Store'
										icon={<ExperienceNavIcon icon={ShoppingBag} />}
										component={Link}
										to={`/${orgSlug}/experiences/${experienceSlug}/manage/store`}
										active={isActive(
											`/${orgSlug}/experiences/${experienceSlug}/manage/store`,
										)}
									/>
									<NavLink
									label='Message Templates'
									icon={<ExperienceNavIcon icon={Template} />}
									component={Link}
									to={`/${orgSlug}/experiences/${experienceSlug}/manage/messagesetups`}
									active={isActive(
										`/${orgSlug}/experiences/${experienceSlug}/manage/messagesetups`,
									)}
								/>
							</NavLink>
						)}

						{(orgRole === 'admin' || userType === 'superAdmin') &&
							!inAdminRoutes && (
								<Box
									mt='xs'
									pt='sm'
									sx={(theme) => ({
										borderTop: `1px solid ${
											theme.colorScheme === 'dark'
												? theme.colors.dark[4]
												: theme.colors.gray[2]
										}`,
									})}
								>
									<NavLink
										label={`Manage ${currentOrgName}`}
										component={Link}
										to={`/${orgSlug}/admin/experiences`}
										active={isActive('/admin/experiences')}
									/>
								</Box>
							)}
					</>
				)}
			</Stack>
		</ScrollArea>
	)
}

export default Sidebar
